import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material'
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Transgender as GenderIcon,
  CalendarToday as CalendarIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material'
import { StyledCard, PrimaryButton } from './ui/StyledComponents'
import SnackbarMessage from './SnackbarMessage'
import { useAuth } from '../context/AuthContext'
import supabase from '../supabaseClient'

function ProfileSettings() {
  const { user, userRole } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    join_date: '',
    message_notifications: true,
  })
  const [originalData, setOriginalData] = useState({})
  const [errors, setErrors] = useState({})
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  // Determine which table to query based on user role
  const getTableName = () => {
    if (userRole === 'super_admin') return 'super_admins'
    if (userRole === 'admin') return 'admins'
    if (userRole === 'teacher') return 'teachers'
    return null
  }

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || !userRole) return

      setLoading(true)
      try {
        const tableName = getTableName()
        if (!tableName) {
          setLoading(false)
          return
        }

        // For super_admins, we need to get data from auth.users
        if (userRole === 'super_admin') {
          const { data: authUser } = await supabase.auth.getUser()
          if (authUser?.user) {
            setProfileData({
              first_name: authUser.user.user_metadata?.first_name || '',
              last_name: authUser.user.user_metadata?.last_name || '',
              email: authUser.user.email || '',
              phone: '',
              gender: '',
            })
            setOriginalData({
              first_name: authUser.user.user_metadata?.first_name || '',
              last_name: authUser.user.user_metadata?.last_name || '',
              email: authUser.user.email || '',
              phone: '',
              gender: '',
            })
          }
        } else {
          // For admins and teachers, fetch from their respective tables
          // Include different fields based on role
          const selectFields = userRole === 'teacher' 
            ? 'first_name, last_name, email, phone, gender, join_date, message_notifications'
            : 'first_name, last_name, email, phone, message_notifications'
          
          const { data, error } = await supabase
            .from(tableName)
            .select(selectFields)
            .eq('id', user.id)
            .maybeSingle()

          if (error) throw error

          if (data) {
            const profile = {
              first_name: data.first_name || '',
              last_name: data.last_name || '',
              email: data.email || '',
              phone: data.phone ? formatPhone(data.phone) : '',
              message_notifications: data.message_notifications !== false, // default to true
            }
            
            // Add teacher-specific fields
            if (userRole === 'teacher') {
              profile.gender = data.gender || ''
              profile.join_date = data.join_date || ''
            }
            
            setProfileData(profile)
            setOriginalData({ ...profile })
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
        setSnackbar({
          open: true,
          message: 'Failed to load profile data. Please try again.',
          severity: 'error',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user, userRole])

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Clear error for this field
    setErrors((prev) => ({ ...prev, [name]: '' }))

    if (name === 'phone') {
      // Format phone number as user types
      const formatted = formatPhone(value)
      setProfileData((prev) => ({ ...prev, phone: formatted }))
      
      // Validate phone length
      const digits = value.replace(/\D/g, '')
      if (digits.length > 0 && digits.length !== 10) {
        setErrors((prev) => ({
          ...prev,
          phone: 'Phone number must be 10 digits',
        }))
      }
    } else if (name === 'first_name' || name === 'last_name') {
      // Only allow letters and spaces
      const letterOnlyValue = value.replace(/[^A-Za-z\s]/g, '')
      setProfileData((prev) => ({
        ...prev,
        [name]: letterOnlyValue,
      }))
    } else if (name === 'message_notifications') {
      // Handle toggle switch
      setProfileData((prev) => ({ ...prev, [name]: !prev[name] }))
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const formatPhone = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '')
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  }

  const validateForm = () => {
    const newErrors = {}

    if (!profileData.first_name.trim()) {
      newErrors.first_name = 'First name is required'
    }

    if (!profileData.last_name.trim()) {
      newErrors.last_name = 'Last name is required'
    }

    // Email is required for admins, optional for teachers
    if (userRole === 'admin' && !profileData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (profileData.email.trim() && !profileData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (profileData.phone) {
      const digits = profileData.phone.replace(/\D/g, '')
      if (digits.length !== 10) {
        newErrors.phone = 'Phone number must be 10 digits'
      }
    }

    // Gender is required for teachers
    if (userRole === 'teacher' && !profileData.gender) {
      newErrors.gender = 'Gender is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const hasChanges = () => {
    return JSON.stringify(profileData) !== JSON.stringify(originalData)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setSaving(true)
    try {
      const tableName = getTableName()
      if (!tableName) {
        throw new Error('Invalid user role')
      }

      // For super_admins, update auth metadata
      if (userRole === 'super_admin') {
        const { error: updateError } = await supabase.auth.updateUser({
          email: profileData.email.trim(),
          data: {
            first_name: profileData.first_name.trim(),
            last_name: profileData.last_name.trim(),
          },
        })

        if (updateError) throw updateError
      } else {
        // For admins and teachers, update their table
        const updateData = {
          first_name: profileData.first_name.trim(),
          last_name: profileData.last_name.trim(),
          email: profileData.email.trim() || null, // Allow null for teachers
          phone: profileData.phone ? profileData.phone.replace(/\D/g, '') : null,
          message_notifications: profileData.message_notifications,
          updated_at: new Date().toISOString(),
        }

        // Add gender for teachers (don't update join_date - it's set by admin)
        if (userRole === 'teacher') {
          updateData.gender = profileData.gender || null
        }

        const { error: updateError } = await supabase
          .from(tableName)
          .update(updateData)
          .eq('id', user.id)

        if (updateError) throw updateError

        // If email changed and is not empty, update auth email
        if (profileData.email !== originalData.email && profileData.email.trim()) {
          const { error: emailError } = await supabase.auth.updateUser({
            email: profileData.email.trim(),
          })

          if (emailError) {
            console.error('Error updating email:', emailError)
            // Don't throw - profile update succeeded, email update failed
            setSnackbar({
              open: true,
              message: 'Profile updated, but email update requires verification. Check your email.',
              severity: 'warning',
            })
            return
          }
        }
      }

      // Update original data to reflect saved state
      setOriginalData({ ...profileData })

      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error updating profile:', error)
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update profile. Please try again.',
        severity: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Card
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
            : '0 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 30px rgba(0, 0, 0, 0.4)' 
              : '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              pb: 2,
              borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            }}
          >
            <Box
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(52, 152, 219, 0.2)' 
                  : 'rgba(52, 152, 219, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon 
                sx={{ 
                  fontSize: { xs: 20, sm: 24 }, 
                  color: theme.palette.primary.main 
                }} 
              />
            </Box>
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  color: theme.palette.text.primary,
                }}
              >
                Profile Information
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mt: 0.5,
                }}
              >
                Update your personal information and preferences
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={profileData.first_name}
                onChange={handleChange}
                required
                error={!!errors.first_name}
                helperText={errors.first_name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={profileData.last_name}
                onChange={handleChange}
                required
                error={!!errors.last_name}
                helperText={errors.last_name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={profileData.email}
                onChange={handleChange}
                required={userRole === 'admin'}
                error={!!errors.email}
                helperText={
                  errors.email || 
                  (userRole === 'admin' 
                    ? 'Required. Changing your email will require verification'
                    : 'Optional. Changing your email will require verification')
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={profileData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                placeholder="(555) 555-5555"
                inputProps={{ maxLength: 14 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>

            {userRole === 'teacher' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: 'relative' }}>
                    <GenderIcon 
                      sx={{ 
                        position: 'absolute',
                        left: 14,
                        top: 18,
                        zIndex: 1,
                        color: theme.palette.text.secondary,
                        fontSize: '1.25rem',
                        pointerEvents: 'none',
                      }} 
                    />
                    <FormControl 
                      fullWidth 
                      required 
                      error={!!errors.gender}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          pl: 5,
                        },
                      }}
                    >
                      <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Gender
                      </InputLabel>
                      <Select
                        name="gender"
                        value={profileData.gender}
                        onChange={handleChange}
                        label="Gender"
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& .MuiSelect-select': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                        }}
                      >
                        <MenuItem value="Male" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Male
                        </MenuItem>
                        <MenuItem value="Female" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Female
                        </MenuItem>
                      </Select>
                      {errors.gender && (
                        <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {errors.gender}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Grid>
                {profileData.join_date && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Join Date"
                      value={profileData.join_date}
                      disabled
                      helperText="Join date is set by administrator"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: '1.25rem',
                              }} 
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          borderRadius: 2,
                        },
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                      }}
                    />
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <Divider 
                sx={{ 
                  my: 2,
                  borderColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.08)',
                }} 
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.03)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(52, 152, 219, 0.2)' 
                        : 'rgba(52, 152, 219, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <NotificationsIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: theme.palette.primary.main 
                      }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profileData.message_notifications}
                          onChange={handleChange}
                          name="message_notifications"
                          color="primary"
                        />
                      }
                      label={
                        <Typography 
                          sx={{ 
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            fontWeight: 500,
                          }}
                        >
                          Enable Message Notifications
                        </Typography>
                      }
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        display: 'block', 
                        mt: 0.5,
                        ml: 4.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      Receive notifications for new messages
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider 
                sx={{ 
                  my: 2,
                  borderColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.08)',
                }} 
              />
            </Grid>

            <Grid item xs={12}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 2,
                  pt: 1,
                }}
              >
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={saving || !hasChanges()}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '10px 24px', sm: '12px 32px' },
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: hasChanges() && !saving
                      ? theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(52, 152, 219, 0.3)'
                        : '0 4px 12px rgba(52, 152, 219, 0.2)'
                      : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: hasChanges() && !saving ? 'translateY(-2px)' : 'none',
                      boxShadow: hasChanges() && !saving
                        ? theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(52, 152, 219, 0.4)'
                          : '0 6px 16px rgba(52, 152, 219, 0.3)'
                        : 'none',
                    },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={isMobile ? 20 : 24} color="inherit" />
                  ) : (
                    'Save Changes'
                  )}
                </PrimaryButton>
              </Box>
            </Grid>
          </Grid>
        </form>
        </CardContent>
      </Card>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </>
  )
}

export default ProfileSettings

